// Nominatim (OpenStreetMap) geocoding, wrapped to respect their usage policy.
//
// SEC-12 was originally recorded as "calls sent with no User-Agent". That fix is
// not possible here: `User-Agent` is a forbidden header name, so browsers ignore
// any attempt to set it from fetch/XHR. For a browser app Nominatim identifies
// the caller by `Referer`, which the browser sends automatically — so the
// identification half of the policy is already satisfied and was never the real
// exposure.
//
// What IS real, and is what this module addresses:
//
//   1. Rate limit — the policy is an absolute maximum of 1 request/second.
//      Nothing throttled these calls, and `searchLocation` fires one per city
//      selection, so clicking through the list quickly exceeded it. The penalty
//      is a 429 and, repeated, an IP block — which would take out delivery-area
//      selection for every customer behind that address.
//
//   2. No caching — the city list is 20 fixed values, so the same handful of
//      lookups were re-requested indefinitely. Caching removes nearly all of
//      the traffic rather than merely pacing it.
//
//   3. `response.json()` was called without checking `response.ok`. An error
//      response body is not JSON, so a 429 surfaced as an opaque parse error
//      rather than "we are being throttled".

const ENDPOINT = 'https://nominatim.openstreetmap.org';
const MIN_INTERVAL_MS = 1100; // policy is 1/sec; a little headroom for clock skew
const CACHE_KEY = 'geocode_cache_v1';
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // city coordinates do not move

let lastRequestAt = 0;
let queue = Promise.resolve();

const readCache = () => {
    try {
        return JSON.parse(localStorage.getItem(CACHE_KEY)) || {};
    } catch {
        return {};
    }
};

const writeCache = (cache) => {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch {
        // Storage full or unavailable — caching is an optimisation, not a requirement.
    }
};

const getCached = (key) => {
    const entry = readCache()[key];
    if (!entry || typeof entry.at !== 'number') return null;
    if (Date.now() - entry.at > CACHE_TTL_MS) return null;
    return entry.value;
};

const setCached = (key, value) => {
    const cache = readCache();
    cache[key] = { value, at: Date.now() };
    writeCache(cache);
};

/**
 * Serialises every outbound request and spaces them at least MIN_INTERVAL_MS
 * apart. Chaining onto a single promise rather than tracking timestamps per call
 * is what makes this hold under concurrency: two rapid clicks queue behind each
 * other instead of both seeing "last request was long ago" and firing together.
 */
const rateLimited = (fn) => {
    const run = async () => {
        const wait = MIN_INTERVAL_MS - (Date.now() - lastRequestAt);
        if (wait > 0) {
            await new Promise((resolve) => setTimeout(resolve, wait));
        }
        lastRequestAt = Date.now();
        return fn();
    };
    // Keep the chain alive even if one call rejects, or a single failure would
    // permanently wedge every later lookup.
    queue = queue.then(run, run);
    return queue;
};

const fetchJson = async (url) => {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) {
        // Distinguish throttling, because it is the failure this module exists to
        // avoid and the one worth seeing in a log.
        if (response.status === 429) {
            throw new Error('Location service is rate limiting us. Please try again shortly.');
        }
        throw new Error(`Location service returned ${response.status}`);
    }
    return response.json();
};

/**
 * Forward geocode a place name to [lat, lon], or null if not found.
 */
export const geocodePlace = async (place) => {
    const key = `q:${String(place).toLowerCase().trim()}`;
    const cached = getCached(key);
    if (cached !== null) return cached;

    const data = await rateLimited(() =>
        fetchJson(`${ENDPOINT}/search?format=json&q=${encodeURIComponent(place)}&limit=1`)
    );

    const result = Array.isArray(data) && data.length > 0
        ? [parseFloat(data[0].lat), parseFloat(data[0].lon)]
        : null;

    // Negative results are cached too — a name that does not resolve will not
    // resolve on the next click either, and re-asking spends the rate limit.
    setCached(key, result);
    return result;
};

/**
 * Reverse geocode coordinates to a city name, or null if none could be derived.
 */
export const reverseGeocodeCity = async (lat, lon) => {
    // Round to ~1 km so nearby fixes share a cache entry; a GPS reading is never
    // byte-identical between calls, which would otherwise make the cache useless.
    const key = `r:${Number(lat).toFixed(2)},${Number(lon).toFixed(2)}`;
    const cached = getCached(key);
    if (cached !== null) return cached;

    const data = await rateLimited(() =>
        fetchJson(`${ENDPOINT}/reverse?format=json&lat=${lat}&lon=${lon}`)
    );

    const city = data?.address?.city || data?.address?.town || data?.address?.village || null;
    setCached(key, city);
    return city;
};
